import clsx from 'clsx'
import { capitalize } from 'lodash'
import { useEffect, useRef, useState } from 'react'

import { Line } from '../../models/Line'
import { Payment } from '../../models/Payment'
import { currencyFormat } from '../../utils/numbers'
import { Icon, IconName } from '../Icon'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { SneakPeek } from '../app/design/SneakPeek'
import { ImageView } from '../common/ImageView'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    payment: Payment
    title?: string
    totalTitle?: string
}

export const getIconFromLineType = (line: Line): IconName => {
    if (line.type === 'custom') {
        return 'calculator'
    }

    return 'tag'
}

function getDisplayName(line: Line) {
    const quantity = line.quantity ?? 1
    if (line.item && line.item?.name) {
        return `${line.item.name}${quantity > 1 ? ` x ${quantity}` : ''}`
    }

    if (line.createdDate) {
        const type = (() => {
            switch (line.type) {
                case 'booking':
                    return 'appointment'
                default:
                    return line.type
            }
        })()
        return `${line.createdDate.format('MMM D, YYYY')} ${capitalize(type)}`
    }

    return line.itemDisplay ?? 'Untitled'
}

function DisplayChargeLine({ line }: { line: Line }) {
    const [isExpanded, setIsExpanded] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (ref.current) {
            setIsExpanded(ref.current.clientHeight > 24)
        }
    }, [ref])

    return (
        <div
            className={clsx(
                'flex justify-between gap-3',
                { 'items-center': !isExpanded },
                { 'items-start': isExpanded }
            )}
        >
            <div className="flex items-center gap-4">
                <ImageView
                    image={line.item?.image}
                    className="size-6 shrink-0 rounded-full"
                    fallbackIcon={{
                        icon: `${getIconFromLineType(line)}.small`,
                        shouldInheritColor: true,
                        background: 'round',
                        className: 'text-primary-700 size-6 shrink-0',
                    }}
                />

                <Label size="md" ref={ref}>
                    {getDisplayName(line)}
                </Label>
            </div>
            <Label size="md">{currencyFormat(line.total, true)}</Label>
        </div>
    )
}

export function NewChargeReceiptView({ payment, title, totalTitle }: Props) {
    return (
        <Card size="sm">
            <Heading size="xs" className="mb-6">
                {title ?? 'Charge summary'}
            </Heading>
            {payment.lines.length > 0 &&
                !(
                    payment.type === 'deposit' &&
                    (Boolean(payment.booking) || Boolean(payment.estimate))
                ) && (
                    <>
                        <div className="flex flex-col gap-4">
                            {payment.lines.map((line, i) => (
                                <DisplayChargeLine key={`${line.id}-${i}`} line={line} />
                            ))}
                        </div>
                        <Divider className="mt-4 mb-[10px]" />
                    </>
                )}
            <WorkflowPage.ReceiptView
                modifierGapSize="small"
                transaction={payment}
                dividerClassName="mt-[10px] mb-[10px]"
                totalDividerClassName="mt-[10px] mb-4"
                receipt={{
                    totalText: totalTitle ?? 'Total',
                    total: payment.chargeAmount ?? 0,
                    modifiers: {
                        subtotal: {
                            amount: payment.total ?? 0,
                        },
                        salesTax: payment.taxAmount
                            ? {
                                  amount: payment.taxAmount ?? 0,
                                  parenthetical:
                                      payment.taxcode !== undefined
                                          ? `${payment.taxcode.rate ?? 0}%`
                                          : null,
                              }
                            : undefined,
                        surcharge: payment.surchargeAmount
                            ? {
                                  amount: payment.surchargeAmount ?? 0,
                              }
                            : undefined,
                        discount: Boolean(payment.discount) ? {} : undefined,
                        gratuity:
                            payment.gratuityAmount !== undefined
                                ? {
                                      amount: payment.gratuityAmount ?? 0,
                                      percent:
                                          payment.gratuityType === 'percent'
                                              ? payment.gratuity
                                              : undefined,
                                  }
                                : undefined,
                        deposit: Boolean(payment.paidDeposit)
                            ? {
                                  amount: payment.paidDeposit ?? 0,
                                  title: 'Paid deposit',
                                  isPaid: true,
                              }
                            : undefined,
                    },
                }}
            />
            {!!payment.total && payment.total > 0 && payment.memo && (
                <>
                    <Divider className="my-4" />
                    <div className="flex gap-4">
                        <Icon
                            icon="note.large"
                            shouldInheritColor
                            className="shrink-0 text-gray-500"
                        />
                        <div className="flex grow flex-col gap-1 overflow-auto">
                            <Paragraph size="md" className="text-gray-700">
                                Receipt memo
                            </Paragraph>
                            <SneakPeek lineClamp={5}>
                                <Paragraph size="md" className="text-gray-600">
                                    {payment.memo}
                                </Paragraph>
                            </SneakPeek>
                        </div>
                    </div>
                </>
            )}
        </Card>
    )
}
